// ADR 10.9.26k — account-scoped tutor history in the browser's asynchronous
// store. Every public operation is guarded: storage refusal removes history,
// never the tutor.

import type { TranscriptRow } from '../useTutorStream'
import { capture } from '../../../telemetry/capture'

const DB_NAME = 'lingara-tutor-v1'
const DB_VERSION = 1
const SESSIONS = 'sessions'
const TURNS = 'turns'

export interface ChatSettings {
  persistSectionState?: boolean
}

export interface StoredSession {
  id: string
  userId: string
  title: string
  startedAtMs: number
  updatedAtMs: number
  sourceLang: string
  targetLang: string
  pinnedAt?: number
  manualTitle?: string
  chatSettings?: ChatSettings
  sectionState?: Record<string, boolean>
}

interface StoredTurn {
  key: string
  userId: string
  sessionId: string
  rows: TranscriptRow[]
}

export interface SessionRecord extends StoredSession {
  rows: TranscriptRow[]
}

export interface SessionStore {
  list(userId: string): Promise<StoredSession[]>
  read(userId: string, id: string): Promise<SessionRecord | null>
  put(session: StoredSession): Promise<void>
  writeRows(userId: string, id: string, rows: TranscriptRow[]): Promise<void>
  delete(userId: string, id: string): Promise<void>
  clearUser(userId: string): Promise<void>
  /** Every account's history — ADR 24.9.26w, the refused cold start. */
  clearAll(): Promise<void>
}

export type SessionStorageOperation =
  | 'list' | 'read' | 'put' | 'write_rows' | 'delete' | 'clear_user' | 'clear_all'
type StorageFailureSink = (operation: SessionStorageOperation) => void

function captureStorageFailure(operation: SessionStorageOperation): void {
  capture('tutor_session_storage_failed', { operation })
}

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result)
    request.onerror = () => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve()
    transaction.onerror = () => reject(transaction.error ?? new Error('IndexedDB failed'))
    transaction.onabort = () => reject(transaction.error ?? new Error('IndexedDB aborted'))
  })
}

export function openTutorDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    try {
      const request = window.indexedDB.open(DB_NAME, DB_VERSION)
      request.onupgradeneeded = () => {
        const db = request.result
        const sessions = db.createObjectStore(SESSIONS, { keyPath: ['userId', 'id'] })
        sessions.createIndex('by-user', 'userId')
        const turns = db.createObjectStore(TURNS, { keyPath: 'key' })
        turns.createIndex('by-user', 'userId')
      }
      request.onsuccess = () => resolve(request.result)
      request.onerror = () => reject(request.error ?? new Error('IndexedDB open failed'))
      request.onblocked = () => reject(new Error('IndexedDB open blocked'))
    } catch (error) {
      reject(error)
    }
  })
}

function key(userId: string, id: string): string {
  return `${userId}\u0000${id}`
}

export function sortSessions(rows: StoredSession[]): StoredSession[] {
  return [...rows].sort((a, b) => {
    if ((a.pinnedAt === undefined) !== (b.pinnedAt === undefined)) {
      return a.pinnedAt === undefined ? 1 : -1
    }
    return b.updatedAtMs - a.updatedAtMs
  })
}

class IndexedDbSessionStore implements SessionStore {
  constructor(
    private readonly open: () => Promise<IDBDatabase>,
    private readonly failure: StorageFailureSink,
  ) {}

  private async safely<T>(
    name: SessionStorageOperation,
    fallback: T,
    operation: (db: IDBDatabase) => Promise<T>,
  ): Promise<T> {
    let db: IDBDatabase | undefined
    try {
      db = await this.open()
      return await operation(db)
    } catch {
      this.failure(name)
      return fallback
    } finally {
      db?.close()
    }
  }

  list(userId: string): Promise<StoredSession[]> {
    return this.safely('list', [], async (db) => {
      const tx = db.transaction(SESSIONS, 'readonly')
      const rows = await requestResult(tx.objectStore(SESSIONS).index('by-user').getAll(userId))
      await transactionDone(tx)
      return sortSessions(rows as StoredSession[])
    })
  }

  read(userId: string, id: string): Promise<SessionRecord | null> {
    return this.safely('read', null, async (db) => {
      const tx = db.transaction([SESSIONS, TURNS], 'readonly')
      const sessionRequest = requestResult(tx.objectStore(SESSIONS).get([userId, id]))
      const turnRequest = requestResult(tx.objectStore(TURNS).get(key(userId, id)))
      const [session, turn] = await Promise.all([sessionRequest, turnRequest])
      await transactionDone(tx)
      return session === undefined
        ? null
        : { ...(session as StoredSession), rows: (turn as StoredTurn | undefined)?.rows ?? [] }
    })
  }

  put(session: StoredSession): Promise<void> {
    return this.safely('put', undefined, async (db) => {
      const tx = db.transaction(SESSIONS, 'readwrite')
      const metadata = { ...session } as StoredSession & { rows?: TranscriptRow[] }
      delete metadata.rows
      tx.objectStore(SESSIONS).put(metadata)
      await transactionDone(tx)
    })
  }

  writeRows(userId: string, id: string, rows: TranscriptRow[]): Promise<void> {
    return this.safely('write_rows', undefined, async (db) => {
      const tx = db.transaction(TURNS, 'readwrite')
      tx.objectStore(TURNS).put({ key: key(userId, id), userId, sessionId: id, rows })
      await transactionDone(tx)
    })
  }

  delete(userId: string, id: string): Promise<void> {
    return this.safely('delete', undefined, async (db) => {
      const tx = db.transaction([SESSIONS, TURNS], 'readwrite')
      tx.objectStore(SESSIONS).delete([userId, id])
      tx.objectStore(TURNS).delete(key(userId, id))
      await transactionDone(tx)
    })
  }

  clearUser(userId: string): Promise<void> {
    return this.safely('clear_user', undefined, async (db) => {
      const sessions = await requestResult(
        db.transaction(SESSIONS, 'readonly').objectStore(SESSIONS).index('by-user').getAll(userId),
      ) as StoredSession[]
      const tx = db.transaction([SESSIONS, TURNS], 'readwrite')
      for (const session of sessions) {
        tx.objectStore(SESSIONS).delete([userId, session.id])
        tx.objectStore(TURNS).delete(key(userId, session.id))
      }
      await transactionDone(tx)
    })
  }

  clearAll(): Promise<void> {
    return this.safely('clear_all', undefined, async (db) => {
      const tx = db.transaction([SESSIONS, TURNS], 'readwrite')
      tx.objectStore(SESSIONS).clear()
      tx.objectStore(TURNS).clear()
      await transactionDone(tx)
    })
  }
}

export function createSessionStore(
  open = openTutorDatabase,
  failure: StorageFailureSink = captureStorageFailure,
): SessionStore {
  return new IndexedDbSessionStore(open, failure)
}

export const browserSessionStore = createSessionStore()

/** Called by the shared voluntary/involuntary sign-out path. */
export function clearTutorSessionsForUser(userId: string): Promise<void> {
  return browserSessionStore.clearUser(userId)
}

/**
 * ADR 24.9.26w — a refused cold-start refresh leaves nobody signed in to this
 * browser (it holds one refresh token), so no stored transcript belongs to a
 * live session, whichever account wrote it.
 */
export function clearAllTutorSessions(): Promise<void> {
  return browserSessionStore.clearAll()
}
